import { Alert, Spin, Tag, theme } from 'antd';
import { format, parseISO } from 'date-fns';
import React from 'react';
import { Link } from 'react-router';
import { useGetOfferDetails } from '../../../../utils/api/mpOffers/useGetOfferDetails';
import {
  OFFER_PUBLICATION_STATE,
  OFFER_PUBLISH_STATUS,
} from '../../../../utils/const/mpOffers';

const DASH = '—';

const formatPrice = (value, currency) => {
  if (value == null) return DASH;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(Number(value));
};

const formatDate = (value) => {
  if (!value) return DASH;
  try {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm');
  } catch {
    return DASH;
  }
};

const statusTag = (statuses, value) => {
  if (!value) return DASH;
  const { color, label } = statuses[value] || {
    color: 'default',
    label: value,
  };
  return <Tag color={color}>{label}</Tag>;
};

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 32 }}>
    <div style={{ fontWeight: 600, marginBottom: 12 }}>{title}</div>
    {children}
  </div>
);

const Field = ({ label, children }) => {
  const { token } = theme.useToken();

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
        padding: '10px 12px',
        marginBottom: 4,
        background: token.colorFillAlter,
        borderRadius: token.borderRadiusLG,
        fontSize: '0.8rem',
      }}
    >
      <span style={{ color: '#8c8c8c' }}>{label}</span>
      <span style={{ textAlign: 'right', fontWeight: 'bold' }}>{children}</span>
    </div>
  );
};

const OfferDetail = ({ offerId }) => {
  const { offer, isLoading, isError } = useGetOfferDetails(offerId);

  if (isLoading) return <Spin />;
  if (isError) {
    return (
      <Alert type="error" showIcon message="Impossible de charger l'offre." />
    );
  }
  if (!offer) return null;

  return (
    <div>
      <Section title="Identité de l'offre">
        <Field label="Marketplace">
          {offer.store?.marketplace?.name || DASH}
        </Field>
        <Field label="Store">{offer.store?.name || DASH}</Field>
        <Field label="Statut">
          {statusTag(OFFER_PUBLICATION_STATE, offer.publication_state)}
        </Field>
        <Field label="Statut publish">
          {statusTag(OFFER_PUBLISH_STATUS, offer.publish_status)}
        </Field>
      </Section>

      <Section title="Produit associé">
        <Field label="Nom du produit">{offer.product?.name || DASH}</Field>
        <Field label="Référence">{offer.product?.reference || DASH}</Field>
        <Field label="Marque">{offer.product?.brand || DASH}</Field>
      </Section>

      <Section title="Tarification & stock">
        <Field label="Prix">{formatPrice(offer.price, offer.currency)}</Field>
        <Field label="Stock">
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor:
                  offer.quantity == null
                    ? '#d9d9d9'
                    : offer.quantity === 0
                      ? '#ff4d4f'
                      : offer.quantity > 50
                        ? '#52c41a'
                        : '#fa8c16',
              }}
            />
            {offer.quantity ?? DASH}
          </span>
        </Field>
      </Section>

      <Section title="Discount price">
        <Field label="Prix promo">
          <span style={{ color: '#fa541c' }}>
            {formatPrice(offer.discount_price, offer.currency)}
          </span>
        </Field>
        <Field label="Événement">{offer.event_title || DASH}</Field>
        <Field label="Date de début">
          {formatDate(offer.discount_start_at)}
        </Field>
        <Field label="Date de fin">{formatDate(offer.discount_end_at)}</Field>
      </Section>

      <Section title="Synchronisation">
        <Field label="Dernier envoi API">
          {formatDate(offer.last_pushed_at)}
        </Field>
        <Field label="Prochain envoi">{formatDate(offer.next_push_at)}</Field>
        <Field label="Dernière synchro BO">
          {formatDate(offer.source_updated_at)}
        </Field>
      </Section>

      {offer.product?.reference && (
        <Link
          to={`/dashboard/products/${encodeURIComponent(offer.product.reference)}`}
        >
          Voir la fiche produit →
        </Link>
      )}
    </div>
  );
};

export default OfferDetail;
